import { useId } from "react";

import { DiagramDecisionGate } from "@/app/features/diagrams/components/shared/DiagramDecisionGate";
import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import { DeterministicControlsOutcomes } from "@/app/features/diagrams/components/blog/deterministicControls/DeterministicControlsOutcomes";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

// Dev-mode topology specification check for referential integrity
const pipelineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "validated-input",
    "current-state",
    "allowed-actions",
    "probabilistic-llm",
    "schema-checks",
    "validator-gate",
    "next-state",
    "quarantine-triage",
  ],
  junctions: [],
  connectors: [
    {
      id: "input-to-state",
      source: { nodeId: "validated-input", anchor: "bottom-center" },
      target: { nodeId: "current-state", anchor: "top-center" },
      meaning: "Input flows to current state evaluation",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "state-to-actions",
      source: { nodeId: "current-state", anchor: "bottom-center" },
      target: { nodeId: "allowed-actions", anchor: "top-center" },
      meaning: "Current state defines allowed action constraints",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "actions-to-llm",
      source: { nodeId: "allowed-actions", anchor: "bottom-center" },
      target: { nodeId: "probabilistic-llm", anchor: "top-center" },
      meaning: "Constrained tool context enters probabilistic LLM",
      semanticType: "probabilistic",
      lineStyle: "dashed",
    },
    {
      id: "llm-to-schema",
      source: { nodeId: "probabilistic-llm", anchor: "bottom-center" },
      target: { nodeId: "schema-checks", anchor: "top-center" },
      meaning: "Bounded decision enters schema and transition checks",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "schema-to-gate",
      source: { nodeId: "schema-checks", anchor: "bottom-center" },
      target: { nodeId: "validator-gate", anchor: "top-center" },
      meaning: "Transition checks flow into validation gate",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "gate-to-next-state",
      source: { nodeId: "validator-gate", anchor: "left-center" },
      target: { nodeId: "next-state", anchor: "top-center" },
      meaning: "Valid action executes and updates next state",
      semanticType: "success",
      lineStyle: "solid",
    },
    {
      id: "gate-to-quarantine",
      source: { nodeId: "validator-gate", anchor: "right-center" },
      target: { nodeId: "quarantine-triage", anchor: "top-center" },
      meaning: "Invalid action or failure routed to quarantine triage",
      semanticType: "failure",
      lineStyle: "dashed",
    },
    {
      id: "next-state-feedback",
      source: { nodeId: "next-state", anchor: "left-center" },
      target: { nodeId: "current-state", anchor: "left-center" },
      meaning: "Cycle return updates current state for next loop",
      semanticType: "feedback",
      lineStyle: "dotted",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(pipelineTopologySpec);
}

export function DeterministicControlsArchitectureVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={660}
      height={570}
      title="Deterministic Controls Around Bounded LLM Decision Architecture"
      description="Top-down architecture diagram showing a probabilistic LLM bounded within deterministic state management, action constraints, schema validation, execution loops, and safe fallback triage."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 700 610"
      frame={{
        kind: "boundary",
        label: "DETERMINISTIC CONTROL BOUNDARY",
        x: 20,
        y: 20,
        width: 660,
        height: 570,
      }}
    >
      <DiagramMarkers prefix="dc-" />

      {/* NODE 1: Validated Input (Y: 72 to 120) */}
      <DiagramNode
        x={250}
        y={72}
        width={200}
        height={48}
        label="Validated Input"
        variant="capsule"
      />

      {/* Stem 1 -> Current State */}
      <path
        d="M 350 120 L 350 143"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
        markerEnd="url(#dc-arrow-neutral)"
      />

      {/* NODE 2: Current State (Y: 145 to 197) */}
      <DiagramNode
        x={220}
        y={145}
        width={260}
        height={52}
        label="Current State"
        sublabel="Policy"
        variant="brass"
      />

      {/* Stem 2 -> Allowed Actions */}
      <path
        d="M 350 197 L 350 220"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
        markerEnd="url(#dc-arrow-neutral)"
      />

      {/* NODE 3: Allowed Actions (Y: 222 to 274) */}
      <DiagramNode
        x={220}
        y={222}
        width={260}
        height={52}
        label="Allowed Actions"
        sublabel="Tool Context"
        variant="standard"
      />

      {/* Stem 3 -> Probabilistic LLM (Blue Dashed) */}
      <path
        d="M 350 274 L 350 297"
        fill="none"
        stroke="#3b82f6"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        opacity="0.75"
        markerEnd="url(#dc-arrow-blue)"
      />

      {/* NODE 4: Probabilistic LLM (Y: 299 to 365) */}
      <DiagramNode
        x={210}
        y={299}
        width={280}
        height={66}
        label="Bounded Decision"
        eyebrow="PROBABILISTIC LLM"
        variant="llm"
      />

      {/* Stem 4 -> Schema & Transition Checks */}
      <path
        d="M 350 365 L 350 388"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
        markerEnd="url(#dc-arrow-neutral)"
      />

      {/* NODE 5: Schema + Transition Checks (Y: 390 to 442) */}
      <DiagramNode
        x={200}
        y={390}
        width={300}
        height={52}
        label="Schema + Transition Checks"
        sublabel="Rules & Guards"
        variant="brass"
      />

      {/* Stem 5 -> Validator Gate */}
      <path
        d="M 350 442 L 350 455"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.4"
      />

      {/* NODE 6: Decision Gate (Diamond centered at X=350, Y=475) */}
      <DiagramDecisionGate x={312} y={455} label="VALID?" />

      <DeterministicControlsOutcomes />
    </DiagramFrame>
  );
}
